import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Clock,
  Download,
  FileText,
  Loader2,
  Lock,
  PoundSterling,
  RefreshCw,
  ShieldCheck,
  Target,
  TrendingUp,
  Users,
} from 'lucide-react';
import { ApiService } from '../services/api';
import { useToast } from '../context/ToastContext';
import { TrialSavingsReportData, TrialSavingsReportType } from '../types';

const currency = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  maximumFractionDigits: 0,
});

const formatCurrency = (value: number) => currency.format(value || 0);

const formatMinutes = (minutes: number) => {
  if (minutes < 60) return `${minutes} min`;
  const hours = minutes / 60;
  return `${Number.isInteger(hours) ? hours : hours.toFixed(1)} hrs`;
};

const reportLabel = (type: TrialSavingsReportType) =>
  type === '3pcn_snapshot' ? '3-PCN snapshot' : '10-PCN savings report';

interface TrialSavingsReportProps {
  embedded?: boolean;
}

const TrialSavingsReport: React.FC<TrialSavingsReportProps> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [reportType, setReportType] = useState<TrialSavingsReportType>('3pcn_snapshot');
  const [report, setReport] = useState<TrialSavingsReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadReport = async (type = reportType) => {
    try {
      setLoading(true);
      setError(null);
      const data = await ApiService.getTrialSavingsReport(type);
      setReport(data);
    } catch (err: any) {
      const message = err?.message || 'Unable to load FleetMode trial report.';
      setError(message);
      addToast(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadReport(reportType);
  }, [reportType]);

  const unlocked = report?.extensionStatus === 'unlocked';
  const extensionRequested = report?.extensionStatus === 'requested';
  const missingPcnCount = Math.max((report?.targetCount || 3) - (report?.availablePcnCount || 0), 0);

  const maturity = useMemo(() => {
    if (!report) return [];
    return [
      {
        label: 'Liability ready',
        value: `${report.metrics.liabilityReadyCount}/${report.rows.length}`,
        good: report.metrics.liabilityReadyCount >= Math.max(1, Math.floor(report.rows.length * 0.6)),
      },
      {
        label: 'Recovery visible',
        value: `${report.metrics.recoveryVisibleCount}/${report.rows.length}`,
        good: report.metrics.recoveryVisibleCount >= Math.max(1, Math.floor(report.rows.length * 0.5)),
      },
      {
        label: 'High-risk deadlines',
        value: String(report.metrics.deadlineRisksCount),
        good: report.metrics.deadlineRisksCount === 0,
      },
    ];
  }, [report]);

  const requestExtension = async () => {
    try {
      setRequesting(true);
      await ApiService.requestTrialSavingsExtension();
      addToast('10-PCN savings report request sent for FleetMode team review.', 'success');
      await loadReport('3pcn_snapshot');
    } catch (err: any) {
      addToast(err?.message || 'Unable to request the 10-PCN report.', 'error');
    } finally {
      setRequesting(false);
    }
  };

  const exportCsv = () => {
    if (!report) return;
    const header = [
      'PCN',
      'VRN',
      'Issue date',
      'Authority',
      'Location',
      'Amount',
      'Status',
      'Deadline risk',
      'Liability ready',
      'Recovery visible',
      'Driver or hirer',
      'Evidence gap',
    ];
    const lines = report.rows.map((row) => [
      row.pcnNumber,
      row.vrn,
      row.issueDate || '',
      row.authority,
      row.location,
      row.amount,
      row.status,
      row.deadlineRisk,
      row.liabilityReady ? 'yes' : 'no',
      row.recoveryVisible ? 'yes' : 'no',
      row.driverOrHirer,
      row.evidenceGap || '',
    ]);
    const csv = [header, ...lines]
      .map((line) => line.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `fleetmode-${report.reportType}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  if (loading && !report) {
    return (
      <div className="flex min-h-[480px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className={embedded ? "flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between" : "flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between"}>
        <div>
          {!embedded && (
            <div className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-bold uppercase tracking-wide text-primary-700 dark:border-primary-900/60 dark:bg-primary-950/30 dark:text-primary-300">
              <FileText size={14} />
              FleetMode trial report
            </div>
          )}
          <h1 className={`${embedded ? 'text-xl' : 'mt-4 text-3xl'} font-bold tracking-tight text-slate-950 dark:text-white`}>
            {report ? reportLabel(report.reportType) : 'Trial savings report'}
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
            A board-ready view of confirmed PCNs: cost exposure, time saved, liability readiness, recovery visibility, and the exact gaps the team should close before scaling.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setReportType('3pcn_snapshot')}
            className={`rounded-lg px-4 py-2 text-sm font-bold transition ${
              reportType === '3pcn_snapshot'
                ? 'bg-slate-950 text-white dark:bg-white dark:text-slate-950'
                : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800'
            }`}
          >
            3-PCN snapshot
          </button>
          <button
            onClick={() => unlocked && setReportType('10pcn_savings_report')}
            disabled={!unlocked}
            className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-bold transition ${
              reportType === '10pcn_savings_report'
                ? 'bg-slate-950 text-white dark:bg-white dark:text-slate-950'
                : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800'
            }`}
          >
            {!unlocked && <Lock size={14} />}
            10-PCN report
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200">
          {error}
        </div>
      )}

      {report && !report.canViewReport && (
        <section className="rounded-lg border border-amber-200 bg-amber-50 p-6 dark:border-amber-900/60 dark:bg-amber-950/30">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-lg font-bold text-amber-950 dark:text-amber-100">Manual unlock required</h2>
              <p className="mt-1 text-sm leading-6 text-amber-900 dark:text-amber-200">
                The 10-PCN report requires manual review. It unlocks after the operator has completed the 3-PCN snapshot and the FleetMode team approves a larger sample.
              </p>
            </div>
            <button
              onClick={() => setReportType('3pcn_snapshot')}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-amber-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-amber-950"
            >
              View 3-PCN snapshot
              <ArrowRight size={16} />
            </button>
          </div>
        </section>
      )}

      {report?.canViewReport && (
        <>
          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {[
              { label: 'Cost exposure', value: formatCurrency(report.metrics.totalCostExposure), icon: PoundSterling, accent: 'text-emerald-600' },
              { label: 'Time saved', value: formatMinutes(report.metrics.estimatedTimeSavedMinutes), icon: Clock, accent: 'text-blue-600' },
              { label: 'Liability ready', value: `${report.metrics.liabilityReadyCount}/${report.rows.length}`, icon: ShieldCheck, accent: 'text-violet-600' },
              { label: 'Evidence gaps', value: String(report.metrics.evidenceGapCount), icon: AlertTriangle, accent: 'text-amber-600' },
            ].map((item) => (
              <div key={item.label} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">{item.label}</p>
                  <item.icon size={18} className={item.accent} />
                </div>
                <p className="mt-3 text-3xl font-bold text-slate-950 dark:text-white">{item.value}</p>
              </div>
            ))}
          </section>

          <section className="grid gap-6 xl:grid-cols-[1.5fr_1fr]">
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="flex flex-col gap-3 border-b border-slate-200 p-5 dark:border-slate-800 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-lg font-bold text-slate-950 dark:text-white">PCN clarity table</h2>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    {report.rows.length}/{report.targetCount} PCNs included. {missingPcnCount > 0 ? `${missingPcnCount} more confirmed PCN${missingPcnCount === 1 ? '' : 's'} needed for a complete sample.` : 'Sample complete.'}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => loadReport(reportType)}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    <RefreshCw size={15} />
                    Refresh
                  </button>
                  <button
                    onClick={exportCsv}
                    className="inline-flex items-center gap-2 rounded-lg bg-slate-950 px-3 py-2 text-sm font-bold text-white hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
                  >
                    <Download size={15} />
                    CSV
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-800">
                  <thead className="bg-slate-50 dark:bg-slate-950/50">
                    <tr>
                      {['PCN', 'Vehicle', 'Exposure', 'Risk', 'Liability', 'Recovery', 'Gap'].map((heading) => (
                        <th key={heading} className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {report.rows.map((row) => (
                      <tr key={row.id} className="align-top">
                        <td className="px-4 py-4">
                          <p className="font-bold text-slate-950 dark:text-white">{row.pcnNumber}</p>
                          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{row.authority}</p>
                        </td>
                        <td className="px-4 py-4">
                          <p className="font-semibold text-slate-800 dark:text-slate-200">{row.vrn || 'Unknown'}</p>
                          <p className="mt-1 max-w-[220px] text-xs text-slate-500 dark:text-slate-400">{row.location}</p>
                        </td>
                        <td className="px-4 py-4 font-bold text-slate-950 dark:text-white">{formatCurrency(row.amount)}</td>
                        <td className="px-4 py-4">
                          <span className={`rounded-full px-2 py-1 text-xs font-bold capitalize ${
                            row.deadlineRisk === 'high'
                              ? 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-200'
                              : row.deadlineRisk === 'medium'
                                ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-200'
                                : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200'
                          }`}>
                            {row.deadlineRisk}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center gap-1 text-xs font-bold ${row.liabilityReady ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-500 dark:text-slate-400'}`}>
                            {row.liabilityReady ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
                            {row.liabilityReady ? row.driverOrHirer : 'Unassigned'}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`text-xs font-bold ${row.recoveryVisible ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300'}`}>
                            {row.recoveryVisible ? 'Visible' : 'Not visible'}
                          </span>
                        </td>
                        <td className="px-4 py-4 max-w-xs text-xs text-slate-500 dark:text-slate-400">{row.evidenceGap || 'None'}</td>
                      </tr>
                    ))}
                    {report.rows.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-500 dark:text-slate-400">
                          No confirmed PCNs are available for this report yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <aside className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="flex items-center gap-2">
                  <Target className="text-primary-600" size={18} />
                  <h2 className="font-bold text-slate-950 dark:text-white">What this proves</h2>
                </div>
                <div className="mt-4 space-y-3">
                  {maturity.map((item) => (
                    <div key={item.label} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 dark:bg-slate-950/50">
                      <span className="text-sm text-slate-600 dark:text-slate-300">{item.label}</span>
                      <span className={`text-sm font-bold ${item.good ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300'}`}>{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="flex items-center gap-2">
                  <TrendingUp className="text-emerald-600" size={18} />
                  <h2 className="font-bold text-slate-950 dark:text-white">Plan recommendation</h2>
                </div>
                <p className="mt-3 text-2xl font-bold text-slate-950 dark:text-white">{report.recommendedPlan.name}</p>
                <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">{report.recommendedPlan.reason}</p>
                <button
                  onClick={() => navigate('/pricing')}
                  className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-primary-700"
                >
                  Compare plans
                  <ArrowRight size={16} />
                </button>
              </div>

              <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="flex items-center gap-2">
                  <Users className="text-blue-600" size={18} />
                  <h2 className="font-bold text-slate-950 dark:text-white">Manual extension</h2>
                </div>
                {unlocked ? (
                  <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
                    The FleetMode team has unlocked the 10-PCN report for this organisation.
                  </p>
                ) : extensionRequested ? (
                  <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
                    The request is logged. The FleetMode team will review the snapshot before unlocking the larger report.
                  </p>
                ) : (
                  <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
                    Use this only when an operator likes the trial but wants to test a few more PCNs before committing.
                  </p>
                )}
                <button
                  onClick={requestExtension}
                  disabled={!report.canRequestExtension || requesting}
                  className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  {requesting ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
                  {extensionRequested ? 'Request already logged' : 'Request 10-PCN unlock'}
                </button>
              </div>
            </aside>
          </section>
        </>
      )}
    </div>
  );
};

export default TrialSavingsReport;
